/**
 * The "needs a look" checks: entries that look forgotten, judged against each
 * baby's own recent log. The overview shows them on the phone and the MCP
 * `list_alerts` tool reports them to an assistant; each surface words them
 * for its reader, so these return what was seen, not sentences.
 *
 * The checks are about the log, not the baby: "no bottle logged in 4 hr" asks
 * whether a parent forgot to tap, never whether the baby is fed enough.
 */
import { type Calendar, localCalendar } from "./calendar.ts";
import { isTracked, type TrackableType } from "./tracking.ts";

const MIN = 60_000;
const HOUR = 60 * MIN;

/** A completed sleep longer than this is almost certainly a forgotten Wake up tap. */
export const MAX_PLAUSIBLE_SLEEP_MS = 14 * HOUR;
/**
 * Calendar days of history the checks expect (today included): the overview's
 * two weeks, from local midnight 14 days back. "Longer than any stretch
 * lately" means within this window.
 */
export const HISTORY_DAYS = 14;

export type EntryType = "feed" | "diaper" | "sleep" | "meds" | "note";

/** The fields the checks read; the app's and the MCP package's entries both fit. */
export interface CheckedEntry {
  id: string;
  babyId: string;
  type: EntryType;
  at: Date;
  endedAt: Date | null;
  label: string | null;
}

/** Something that looks forgotten, with the entries that made it look that way. */
export type Finding<E extends CheckedEntry = CheckedEntry> = { key: string; babyId: string } & (
  | {
      /** A nap still running though a bottle or change was logged after it began. */
      kind: "open-nap";
      nap: E;
      after: E;
    }
  | {
      /** A nap running longer than any logged lately (or 4 hr with none to compare). */
      kind: "long-nap";
      nap: E;
      /** The longest plausible stretch in the history, if any. */
      longestMs: number | null;
    }
  | {
      /** A twin's bottle or change just logged, and nothing for this baby in a while. */
      kind: "twin";
      type: "feed" | "diaper";
      /** This baby's last of that type. */
      last: E;
      sibling: { babyId: string; last: E };
    }
  | {
      /** No bottle or change for well past this baby's usual gap. */
      kind: "overdue";
      type: "feed" | "diaper";
      last: E;
      /** The usual gap lately, rounded to five minutes. */
      usualGapMin: number;
    }
  | {
      /** A medicine given most days, not yet logged today and an hour past its usual time. */
      kind: "meds";
      label: string;
      /** Today at the usual clock time. */
      usualAt: Date;
      lastDose: E;
    }
  | {
      /** A finished sleep longer than any real one: a missed Wake up tap. */
      kind: "sleep-too-long";
      sleep: E;
    }
);

/** How far back the pattern checks look for a baby's rhythm. */
export const PATTERN_MS = 72 * HOUR;
/** A twin's entry this close to one of this baby's counts as done together. */
const TOGETHER_MS = 30 * MIN;
/**
 * The oldest entry the bottle and change checks read, beyond a nap still
 * open: `PATTERN_MS`, plus the half hour before it, where a twin's entry can
 * match one inside it. Each check needs at least four of the baby's entries
 * inside `PATTERN_MS`. A caller that keeps only the bottle findings (the
 * bottle alerts) may pass only the feeds and sleeps since this.
 */
export const PATTERN_HISTORY_MS = PATTERN_MS + TOGETHER_MS;
/** Gaps shorter than this are a top-up or a split bottle, not a new feed. */
const MIN_GAP_MS = 20 * MIN;

function median(values: number[]): number | null {
  if (!values.length) return null;
  const s = [...values].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid]! : (s[mid - 1]! + s[mid]!) / 2;
}

/** The usual time between entries of a type, if there are enough to say. */
function usualGap(list: readonly CheckedEntry[], now: Date): number | null {
  const times = list
    .map((e) => e.at.getTime())
    .filter((t) => t > now.getTime() - PATTERN_MS)
    .sort((a, b) => a - b);
  const gaps: number[] = [];
  for (let i = 1; i < times.length; i++) {
    const gap = times[i]! - times[i - 1]!;
    if (gap >= MIN_GAP_MS) gaps.push(gap);
  }
  return gaps.length >= 4 ? median(gaps) : null;
}

/** Share of this baby's recent entries one sibling matched within half an hour. */
function syncShare(mine: readonly CheckedEntry[], theirs: readonly CheckedEntry[], now: Date) {
  const recent = mine.filter((e) => e.at.getTime() > now.getTime() - PATTERN_MS);
  if (recent.length < 4) return 0;
  const matched = recent.filter((e) =>
    theirs.some((s) => Math.abs(s.at.getTime() - e.at.getTime()) <= TOGETHER_MS),
  );
  return matched.length / recent.length;
}

const roundToFive = (ms: number) => Math.max(5, Math.round(ms / (5 * MIN)) * 5);

/**
 * Entries that look forgotten, per baby in tab order: a nap left running, a
 * bottle or change overdue against the baby's own recent rhythm, one twin
 * logged without the other, a daily medicine not yet given, and a sleep so
 * long it must be a missed Wake up tap. Pass the `HISTORY_DAYS` window (plus
 * any nap still open); with less, the checks have less to compare against.
 *
 * A type the household turned off (`untracked`) raises nothing: with sleep
 * off, an open nap counts as absent, so it no longer silences the bottle and
 * change checks. A change logged anyway still shows a nap's baby woke.
 */
export function findForgotten<E extends CheckedEntry>(
  entries: readonly E[],
  babies: readonly { id: string }[],
  now: Date,
  calendar: Calendar = localCalendar,
  untracked: readonly TrackableType[] = [],
): Finding<E>[] {
  const out: Finding<E>[] = [];
  const n = now.getTime();
  const of = (babyId: string, type: EntryType) =>
    entries
      .filter((e) => e.babyId === babyId && e.type === type)
      .sort((a, b) => b.at.getTime() - a.at.getTime());

  for (const baby of babies) {
    const sleeps = isTracked(untracked, "sleep") ? of(baby.id, "sleep") : [];
    const open = sleeps.find((e) => e.endedAt === null);

    // 1. A nap left running.
    if (open) {
      const after = entries
        .filter(
          (e) =>
            e.babyId === baby.id &&
            (e.type === "feed" || e.type === "diaper") &&
            e.at.getTime() > open.at.getTime() + 10 * MIN,
        )
        .sort((a, b) => a.at.getTime() - b.at.getTime())[0];
      const longest = Math.max(
        0,
        ...sleeps
          .filter(
            (e) => e.endedAt && e.endedAt.getTime() - e.at.getTime() <= MAX_PLAUSIBLE_SLEEP_MS,
          )
          .map((e) => e.endedAt!.getTime() - e.at.getTime()),
      );
      const asleep = n - open.at.getTime();
      if (after) {
        out.push({
          key: `${baby.id}:open-nap`,
          babyId: baby.id,
          kind: "open-nap",
          nap: open,
          after,
        });
      } else if (asleep >= (longest ? Math.max(longest, 3 * HOUR) : 4 * HOUR)) {
        out.push({
          key: `${baby.id}:long-nap`,
          babyId: baby.id,
          kind: "long-nap",
          nap: open,
          longestMs: longest || null,
        });
      }
    }

    // 2. A bottle or change logged for a twin and not this baby, or overdue
    // against this baby's own rhythm. Asleep, neither is expected.
    for (const type of (["feed", "diaper"] as const).filter((t) => isTracked(untracked, t))) {
      const mine = of(baby.id, type);
      const last = mine[0];
      if (!last || open) continue;

      // The newest sibling logged lately whose entries usually match this
      // baby's. Each sibling counts on its own: pooled, triplets or more would
      // match almost any entry, and a baby on its own rhythm would look in sync.
      let sibling: { babyId: string; last: E } | undefined;
      for (const other of babies) {
        if (other.id === baby.id) continue;
        const theirs = of(other.id, type);
        const latest = theirs[0];
        if (!latest || n - latest.at.getTime() > 45 * MIN) continue;
        if (latest.at.getTime() - last.at.getTime() < 90 * MIN) continue;
        if (sibling && latest.at.getTime() <= sibling.last.at.getTime()) continue;
        if (syncShare(mine, theirs, now) >= 0.5) sibling = { babyId: other.id, last: latest };
      }
      if (sibling) {
        out.push({
          key: `${baby.id}:${type}:twin`,
          babyId: baby.id,
          kind: "twin",
          type,
          last,
          sibling,
        });
        continue;
      }

      const gap = usualGap(mine, now);
      if (gap === null) continue;
      const floor = type === "feed" ? 2.5 * HOUR : 3 * HOUR;
      const factor = type === "feed" ? 1.3 : 1.5;
      if (n - last.at.getTime() < Math.max(gap * factor, floor)) continue;
      out.push({
        key: `${baby.id}:${type}:overdue`,
        babyId: baby.id,
        kind: "overdue",
        type,
        last,
        usualGapMin: roundToFive(gap),
      });
    }

    // 3. A daily medicine not given yet today.
    const meds = of(baby.id, "meds").filter((e) => e.label);
    const labels = [...new Set(meds.map((e) => e.label!))];
    for (const label of labels) {
      const doses = meds.filter((e) => e.label === label);
      if (doses.some((e) => calendar.sameDay(e.at, now))) continue;
      // The first dose each day as a clock time (minutes past midnight), not
      // time elapsed since midnight, which a DST day shifts by an hour.
      const firstMinutes: number[] = [];
      for (let k = 1; k <= 4; k++) {
        const day = calendar.addDays(now, -k);
        const ofDay = doses.filter((e) => calendar.sameDay(e.at, day));
        if (!ofDay.length) continue;
        const first = ofDay.reduce((a, e) => (e.at < a.at ? e : a));
        firstMinutes.push(calendar.minuteOfDay(first.at));
      }
      if (firstMinutes.length < 3) continue;
      const usualAt = calendar.atMinute(now, Math.round(median(firstMinutes)!));
      if (n < usualAt.getTime() + HOUR) continue;
      out.push({
        key: `${baby.id}:meds:${label}`,
        babyId: baby.id,
        kind: "meds",
        label,
        usualAt,
        lastDose: doses[0]!,
      });
    }

    // 4. A finished sleep too long to be real.
    for (const e of sleeps) {
      if (!e.endedAt || n - e.endedAt.getTime() > PATTERN_MS) continue;
      if (e.endedAt.getTime() - e.at.getTime() <= MAX_PLAUSIBLE_SLEEP_MS) continue;
      out.push({
        key: `${baby.id}:sleep-too-long:${e.id}`,
        babyId: baby.id,
        kind: "sleep-too-long",
        sleep: e,
      });
    }
  }
  return out;
}
